import type { SimConfig } from '../config';
import type { EventSink } from '../events';
import { inCross, isFreeCell } from '../geometry';
import { nextInt } from '../rng';
import type { BonusState, MatchState } from '../types';

const OVERLAP_MARGIN = 0.1;

interface BonusCell {
  col: number;
  row: number;
  overlap: boolean;
  inLethal: boolean;
  forbiddenRow: boolean;
  nearPlayer: boolean;
}

/** Strictest first, like golden respawns. Never on top of a body if avoidable. */
const LEVELS: readonly ((c: BonusCell) => boolean)[] = [
  (c) => !c.overlap && !c.inLethal && !c.forbiddenRow && !c.nearPlayer,
  (c) => !c.overlap && !c.inLethal,
  (c) => !c.overlap,
  () => true,
];

function chooseBonusCell(state: MatchState, config: SimConfig): { col: number; row: number } {
  const cells: BonusCell[] = [];
  const br = config.bonus.radius;
  for (let row = 0; row < config.rows; row++) {
    for (let col = 0; col < config.columns; col++) {
      if (!isFreeCell(config, col, row)) continue;
      const x = col + 0.5;
      const y = row + 0.5;
      let overlap = false;
      let nearPlayer = false;
      for (const p of state.players) {
        const d = Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2);
        if (d < config.player.radius + br + OVERLAP_MARGIN) overlap = true;
        if (d < config.respawn.minPlayerDistance) nearPlayer = true;
      }
      for (const g of state.goldens) {
        if (g.status === 'ABSENT') continue;
        const d = Math.sqrt((g.x - x) ** 2 + (g.y - y) ** 2);
        if (d < config.golden.radius + br + OVERLAP_MARGIN) overlap = true;
      }
      const inLethal = state.explosions.some(
        (e) => state.tick < e.lethalUntilTick && inCross(e.cross, x, y),
      );
      cells.push({
        col,
        row,
        overlap,
        inLethal,
        forbiddenRow: config.map.forbiddenSpawnRows.includes(row),
        nearPlayer,
      });
    }
  }
  for (const accept of LEVELS) {
    const candidates = cells.filter(accept);
    if (candidates.length > 0) return candidates[nextInt(state, candidates.length)] as BonusCell;
  }
  throw new Error('Map has no free cell');
}

/**
 * 1Up pickups. During regulation time one appears on a random free cell every
 * interval and stays for its lifetime. An awake ball touching it gives its own
 * totem one more life, up to the totem's maximum (above the starting lives). Camps are checked
 * ORANGE first, so a simultaneous touch goes to ORANGE.
 */
export function resolveBonuses(state: MatchState, config: SimConfig, sink: EventSink) {
  const tick = sink.tick;
  const reach = config.player.radius + config.bonus.radius;
  const kept: BonusState[] = [];
  for (const bonus of state.bonuses) {
    const taker = state.players.find(
      (p) =>
        p.status !== 'STUNNED' && Math.sqrt((p.x - bonus.x) ** 2 + (p.y - bonus.y) ** 2) < reach,
    );
    if (taker) {
      const totem = state.totems[taker.camp === 'ORANGE' ? 0 : 1];
      totem.hp = Math.min(config.totemMaxHp, totem.hp + 1);
      sink.emit({
        type: 'BONUS_COLLECTED',
        bonusId: bonus.id,
        kind: bonus.kind,
        camp: taker.camp,
        hpAfter: totem.hp,
      });
    } else if (tick >= bonus.expiresAtTick) {
      sink.emit({ type: 'BONUS_EXPIRED', bonusId: bonus.id });
    } else {
      kept.push(bonus);
    }
  }
  if (kept.length !== state.bonuses.length) state.bonuses = kept;

  const clock = state.clock;
  if (clock.phase !== 'REGULATION' || tick < clock.nextBonusTick) return;
  clock.nextBonusTick = tick + config.ticks.bonusInterval;
  const cell = chooseBonusCell(state, config);
  const bonus: BonusState = {
    id: `bonus-${state.nextBonusId++}`,
    kind: 'ONE_UP',
    x: cell.col + 0.5,
    y: cell.row + 0.5,
    spawnedAtTick: tick,
    expiresAtTick: tick + config.ticks.bonusLifetime,
  };
  state.bonuses.push(bonus);
  sink.emit({
    type: 'BONUS_SPAWNED',
    bonusId: bonus.id,
    kind: bonus.kind,
    col: cell.col,
    row: cell.row,
  });
}
